import { useState } from 'react';
import { P } from '@vagusx/shared';
import type { AppointmentResponse } from '@vagusx/clients';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EhrSyncStatus } from '@/components/ehr/EhrSyncStatus';
import { useHasPermission } from '@/hooks/use-permissions';
import { appointmentEhrSyncState, resyncWouldBook } from '@/lib/ehr-sync-state';
import { useAppointmentEhrResync } from '@/scheduling/hooks/use-appointment-ehr-resync';

type SyncedAppointment = Pick<
  AppointmentResponse,
  | 'id'
  | 'status'
  | 'ehrAppointmentId'
  | 'ehrSyncStatus'
  | 'ehrSyncError'
  | 'ehrSyncErrorCode'
  | 'lastEhrSyncAt'
>;

interface AppointmentEhrSyncStatusProps {
  appointment: SyncedAppointment;
  className?: string;
}

/**
 * An appointment's EHR sync status, with Re-sync beside a failure (ADR-284).
 * Renders nothing for an appointment with no EHR sync to show. A retry that
 * would book the appointment asks first: staff may already have booked it by
 * hand after the failure.
 */
export function AppointmentEhrSyncStatus({
  appointment,
  className,
}: AppointmentEhrSyncStatusProps): React.ReactElement | null {
  const canEdit = useHasPermission(P.APPOINTMENTS_EDIT);
  const resync = useAppointmentEhrResync();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const state = appointmentEhrSyncState(appointment);
  if (!state) return null;

  const start = (): void => {
    if (resyncWouldBook(appointment)) setConfirmOpen(true);
    else resync.mutate(appointment.id);
  };

  // React events bubble out of the dialog's portal to its React parents, so the
  // wrapper keeps every click here (dialog included) from reaching a clickable row.
  const stop = (e: React.SyntheticEvent): void => e.stopPropagation();

  return (
    <div className={className} onClick={stop} onKeyDown={stop}>
      <EhrSyncStatus
        state={state}
        onResync={canEdit ? start : undefined}
        isResyncing={resync.isPending}
      />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Book this appointment in the EHR again?</AlertDialogTitle>
            <AlertDialogDescription>
              The earlier attempt didn&apos;t reach the EHR. If someone has since booked it there by
              hand, this books the patient a second time. Check the EHR first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                resync.mutate(appointment.id);
              }}
            >
              Book in EHR
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
